/**
 * Planner · Disponibilidad (A2) — proyectos de asignación (AllocationProject),
 * franjas de disponibilidad por recurso (distintas de las reservas) y un informe
 * que imputa las horas fichadas a cada proyecto (cruce presencia↔disponibilidad
 * por NIF+fecha). Org-scoped. Gated por planner:manage (editar) / read (ver).
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon, PencilSquareIcon, FolderIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common';
import {
  plannerApi,
  type AllocationProject,
  type ResourceAvailability,
  type AllocationReport,
  type PlannerResource,
} from '@/services/api';
import { useOrganizationStore } from '@/stores/organization';

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

const STATUS_LABEL: Record<AllocationProject['status'], string> = { active: 'Activo', inactive: 'Inactivo', archived: 'Archivado' };
const STATUS_COLOR: Record<AllocationProject['status'], string> = {
  active: 'bg-green-100 text-green-700', inactive: 'bg-gray-100 text-gray-600', archived: 'bg-amber-100 text-amber-700',
};
const fmtDate = (iso?: string | null) => (iso ? iso.slice(0, 10) : '—');
const fmtHours = (h: number) => `${h.toFixed(2)} h`;

export default function AvailabilityTab({ canManage }: { canManage: boolean }) {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [projects, setProjects] = useState<AllocationProject[]>([]);
  const [availability, setAvailability] = useState<ResourceAvailability[]>([]);
  const [resources, setResources] = useState<PlannerResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState<AllocationProject | 'new' | null>(null);
  const [editingSlot, setEditingSlot] = useState<ResourceAvailability | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a, r] = await Promise.all([
        plannerApi.listProjects(),
        plannerApi.listAvailability(),
        plannerApi.getResources().catch(() => ({ data: { data: [] } })),
      ]);
      setProjects((p.data.data || []) as AllocationProject[]);
      setAvailability((a.data.data || []) as ResourceAvailability[]);
      setResources((r.data.data || []) as PlannerResource[]);
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status !== 403) toast.error('No se pudo cargar la disponibilidad');
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedOrgId]);

  const resourceName = (id: string) => resources.find((r) => r._id === id)?.name || '(recurso)';
  const projectName = (id?: string | null) => (id ? (projects.find((p) => p._id === id)?.name || '(proyecto)') : 'Sin asignar');
  const projectColor = (id?: string | null) => (id ? projects.find((p) => p._id === id)?.color : undefined);

  const removeProject = async (p: AllocationProject) => {
    if (!confirm(`¿Eliminar el proyecto "${p.name}"? Las franjas que lo usaban quedarán sin asignar.`)) return;
    try { await plannerApi.removeProject(p._id); toast.success('Proyecto eliminado'); void load(); }
    catch { toast.error('No se pudo eliminar'); }
  };
  const removeSlot = async (s: ResourceAvailability) => {
    if (!confirm('¿Eliminar esta franja de disponibilidad?')) return;
    try { await plannerApi.removeAvailability(s._id); toast.success('Franja eliminada'); void load(); }
    catch { toast.error('No se pudo eliminar'); }
  };

  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>;

  return (
    <div className="space-y-8">
      {/* --- Proyectos --- */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><FolderIcon className="w-5 h-5 text-vida-600" /> Proyectos / unidades de negocio</h2>
          {canManage && (
            <button onClick={() => setEditingProject('new')} className="inline-flex items-center gap-1.5 px-3 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700">
              <PlusIcon className="w-5 h-5" /> Nuevo proyecto
            </button>
          )}
        </div>
        {projects.length === 0 ? (
          <p className="text-sm text-gray-400">Aún no hay proyectos. Créalos para poder asignar la disponibilidad de los recursos.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <div key={p._id} className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.color || '#94a3b8' }} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                      {p.code && <p className="text-xs text-gray-400">{p.code}</p>}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => setEditingProject(p)} className="p-1 text-gray-400 hover:text-vida-600"><PencilSquareIcon className="w-4 h-4" /></button>
                      <button onClick={() => void removeProject(p)} className="p-1 text-gray-400 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_COLOR[p.status]}`}>{STATUS_LABEL[p.status]}</span>
                  {(p.startDate || p.endDate) && <span className="text-[11px] text-gray-400">{fmtDate(p.startDate)} → {fmtDate(p.endDate)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Franjas de disponibilidad --- */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Disponibilidad de recursos</h2>
          {canManage && (
            <button onClick={() => setEditingSlot('new')} className="inline-flex items-center gap-1.5 px-3 py-2 border border-vida-300 text-vida-700 rounded-lg text-sm font-medium hover:bg-vida-50">
              <PlusIcon className="w-5 h-5" /> Nueva franja
            </button>
          )}
        </div>
        {availability.length === 0 ? (
          <p className="text-sm text-gray-400">Sin franjas. Asigna a cada recurso un proyecto y un rango de días (o déjalo sin asignar).</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3">Recurso</th>
                  <th className="py-2 px-3">Proyecto</th>
                  <th className="py-2 px-3">Desde</th>
                  <th className="py-2 px-3">Hasta</th>
                  <th className="py-2 pl-3"></th>
                </tr>
              </thead>
              <tbody>
                {availability.map((s) => (
                  <tr key={s._id} className="border-b border-gray-100">
                    <td className="py-2 pr-3 font-medium text-gray-700">{resourceName(s.resourceId)}</td>
                    <td className="py-2 px-3">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: projectColor(s.projectId) || (s.projectId ? '#94a3b8' : '#e5e7eb') }} />
                        {projectName(s.projectId)}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-600">{fmtDate(s.dateFrom)}</td>
                    <td className="py-2 px-3 text-gray-600">{fmtDate(s.dateTo)}</td>
                    <td className="py-2 pl-3 text-right">
                      {canManage && (
                        <span className="inline-flex items-center gap-1">
                          <button onClick={() => setEditingSlot(s)} className="p-1 text-gray-400 hover:text-vida-600"><PencilSquareIcon className="w-4 h-4" /></button>
                          <button onClick={() => void removeSlot(s)} className="p-1 text-gray-400 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --- Informe de imputación --- */}
      <AllocationReportPanel />

      {editingProject && (
        <ProjectEditor project={editingProject === 'new' ? null : editingProject} onClose={() => setEditingProject(null)} onSaved={() => { setEditingProject(null); void load(); }} />
      )}
      {editingSlot && (
        <AvailabilityEditor slot={editingSlot === 'new' ? null : editingSlot} resources={resources} projects={projects} onClose={() => setEditingSlot(null)} onSaved={() => { setEditingSlot(null); void load(); }} />
      )}
    </div>
  );
}

// ---------- Editor de proyecto ----------

function ProjectEditor({ project, onClose, onSaved }: { project: AllocationProject | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!project;
  const [name, setName] = useState(project?.name || '');
  const [code, setCode] = useState(project?.code || '');
  const [color, setColor] = useState(project?.color || '#3b82f6');
  const [status, setStatus] = useState<AllocationProject['status']>(project?.status || 'active');
  const [startDate, setStartDate] = useState(project?.startDate ? project.startDate.slice(0, 10) : '');
  const [endDate, setEndDate] = useState(project?.endDate ? project.endDate.slice(0, 10) : '');
  const [notes, setNotes] = useState(project?.notes || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('Ponle un nombre al proyecto'); return; }
    setBusy(true);
    try {
      const data = { name: name.trim(), code: code.trim() || undefined, color, status, startDate: startDate || undefined, endDate: endDate || undefined, notes: notes.trim() || undefined };
      if (isEdit && project) await plannerApi.updateProject(project._id, data);
      else await plannerApi.createProject(data);
      toast.success(isEdit ? 'Proyecto guardado' : 'Proyecto creado');
      onSaved();
    } catch (e) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo guardar');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm w-full max-w-md p-6 max-h-[92vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{isEdit ? 'Editar proyecto' : 'Nuevo proyecto'}</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className={labelClass}>Nombre</label><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><label className={labelClass}>Código</label><input className={inputClass} value={code} onChange={(e) => setCode(e.target.value)} /></div>
          <div><label className={labelClass}>Color</label><input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-full h-9 rounded border border-gray-300 p-0.5" /></div>
          <div><label className={labelClass}>Estado</label>
            <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value as AllocationProject['status'])}>
              <option value="active">Activo</option><option value="inactive">Inactivo</option><option value="archived">Archivado</option>
            </select>
          </div>
          <div><label className={labelClass}>Desde</label><input type="date" className={inputClass} value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div className="col-span-2"><label className={labelClass}>Hasta</label><input type="date" className={inputClass} value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
          <div className="col-span-2"><label className={labelClass}>Notas</label><textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Editor de franja de disponibilidad ----------

function AvailabilityEditor({ slot, resources, projects, onClose, onSaved }: {
  slot: ResourceAvailability | null; resources: PlannerResource[]; projects: AllocationProject[]; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!slot;
  const [resourceId, setResourceId] = useState(slot?.resourceId || '');
  const [projectId, setProjectId] = useState(slot?.projectId || '');
  const [dateFrom, setDateFrom] = useState(slot?.dateFrom ? slot.dateFrom.slice(0, 10) : '');
  const [dateTo, setDateTo] = useState(slot?.dateTo ? slot.dateTo.slice(0, 10) : '');
  const [notes, setNotes] = useState(slot?.notes || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!resourceId) { toast.error('Selecciona un recurso'); return; }
    if (!dateFrom || !dateTo) { toast.error('Indica el rango de días'); return; }
    if (dateTo < dateFrom) { toast.error('La fecha de fin es anterior al inicio'); return; }
    setBusy(true);
    try {
      const data = {
        resourceId, projectId: projectId || null,
        dateFrom: `${dateFrom}T00:00:00.000Z`, dateTo: `${dateTo}T00:00:00.000Z`,
        notes: notes.trim() || undefined,
      };
      if (isEdit && slot) await plannerApi.updateAvailability(slot._id, data);
      else await plannerApi.createAvailability(data);
      toast.success(isEdit ? 'Franja guardada' : 'Franja creada');
      onSaved();
    } catch (e) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo guardar');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{isEdit ? 'Editar franja' : 'Nueva franja de disponibilidad'}</h2>
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Recurso</label>
            <select className={inputClass} value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
              <option value="">— selecciona —</option>
              {resources.map((r) => <option key={r._id} value={r._id}>{r.name}{r.employeeNif ? ` · ${r.employeeNif}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Proyecto</label>
            <select className={inputClass} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">Sin asignar</option>
              {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelClass}>Desde</label><input type="date" className={inputClass} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
            <div><label className={labelClass}>Hasta</label><input type="date" className={inputClass} value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
          </div>
          <div><label className={labelClass}>Notas</label><textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Informe de imputación ----------

function AllocationReportPanel() {
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const monthAgo = new Date(today.getTime() - 30 * 24 * 3600_000);
  const [from, setFrom] = useState(iso(monthAgo));
  const [to, setTo] = useState(iso(today));
  const [data, setData] = useState<AllocationReport | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await plannerApi.getAllocationReport({ from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z` });
      setData(r.data.data as AllocationReport);
    } catch (e) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo cargar el informe');
    } finally { setBusy(false); }
  };

  const totalHours = data ? data.report.reduce((s, r) => s + r.hours, 0) : 0;

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Imputación de horas por proyecto</h2>
      <p className="text-xs text-gray-400 mb-3">Cruza los fichajes reales (por NIF y fecha) con el proyecto asignado ese día en la disponibilidad. Los días fichados sin franja asignada van a «Sin asignar».</p>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div><label className={labelClass}>Desde</label><input type="date" className={inputClass} value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><label className={labelClass}>Hasta</label><input type="date" className={inputClass} value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <button onClick={run} disabled={busy} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">{busy ? 'Calculando…' : 'Ver informe'}</button>
      </div>

      {data && (
        data.report.length === 0 ? (
          <p className="text-sm text-gray-400">Sin fichajes en el rango.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3">Proyecto</th>
                  <th className="py-2 px-3 text-right">Días</th>
                  <th className="py-2 px-3 text-right">Horas</th>
                  <th className="py-2 pl-3 text-right">%</th>
                </tr>
              </thead>
              <tbody>
                {data.report.map((r) => (
                  <tr key={r.projectId || 'none'} className="border-b border-gray-100">
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color || (r.projectId ? '#94a3b8' : '#e5e7eb') }} />
                        {r.name}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right text-gray-600">{r.days}</td>
                    <td className="py-2 px-3 text-right font-medium text-gray-900">{fmtHours(r.hours)}</td>
                    <td className="py-2 pl-3 text-right text-gray-500">{totalHours > 0 ? Math.round((r.hours / totalHours) * 100) : 0}%</td>
                  </tr>
                ))}
                <tr className="font-semibold text-gray-900">
                  <td className="py-2 pr-3">Total</td>
                  <td></td>
                  <td className="py-2 px-3 text-right">{fmtHours(totalHours)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
            {!data.rounding && <p className="text-[11px] text-gray-400 mt-2">Horas sin redondeo (el redondeo es opt-in en la config de gk2control).</p>}
          </div>
        )
      )}
    </div>
  );
}
